import { useState, useRef, useCallback, useEffect, useMemo, useId } from 'react'
import * as Popover from '@radix-ui/react-popover'
import * as Checkbox from '@radix-ui/react-checkbox'
import { CheckIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import { ChevronDown } from '@/assets/icons'
import { TypeaheadChip } from './TypeaheadChip'
import { TypeaheadOption } from './TypeaheadOption'
import type { TypeaheadProps, TypeaheadItem } from './types'

/**
 * Typeahead Component
 *
 * A multi-select typeahead with chips displayed inside the input field.
 * Selected items remain visible in the dropdown with a checked checkbox,
 * and are also shown as removable chips in the input area.
 *
 * example usage:
 * ```tsx
 * return (
 *   <Typeahead
 *     items={items}
 *     selectedIds={selectedIds}
 *     onChange={setSelectedIds}
 *     placeholder="Search members..."
 *     selectAllLabel="Select all {count} team members"
 *   />
 * )
 * ```
 */
export function Typeahead({
  items,
  selectedIds,
  onChange,
  placeholder = 'Search...',
  ariaLabel,
  disabled = false,
  className,
  emptyStateTitle,
  emptyStateDescription,
  noResultsTitle = 'No results for "{searchTerm}" found',
  noResultsDescription,
  emptyStateAction,
  autoFocus = false,
  selectAllLabel = 'Select all {count} items',
  showSelectAll = true,
  required = false,
  helperText,
  leadingIcon,
  modal = false,
}: TypeaheadProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const listboxId = useId()
  const inputId = useId()

  // Build lookup map for selected items
  const selectedItemsMap = useMemo(() => {
    const map = new Map<string, TypeaheadItem>()
    for (const item of items) {
      if (selectedIds.includes(item.id)) {
        map.set(item.id, item)
      }
    }
    return map
  }, [items, selectedIds])

  // Filter items by search query (selected items remain visible)
  const filteredItems = useMemo(() => {
    const query = searchQuery.toLowerCase().trim()
    if (!query) return items

    return items.filter((item) => {
      // Match against label or description (case-insensitive)
      const labelMatch = item.label.toLowerCase().includes(query)
      const descriptionMatch = item.description?.toLowerCase().includes(query) ?? false
      return labelMatch || descriptionMatch
    })
  }, [items, searchQuery])

  // Check if all filtered items are selected
  const allFilteredSelected = filteredItems.length > 0 && filteredItems.every((item) => selectedIds.includes(item.id))
  const someFilteredSelected = filteredItems.some((item) => selectedIds.includes(item.id))

  // Format the select all label with count
  const formattedSelectAllLabel = selectAllLabel.replace('{count}', String(filteredItems.length))

  // Reset highlight when filtered items change
  useEffect(() => {
    setHighlightedIndex(-1)
  }, [filteredItems.length])

  // Handle toggling an item (select/deselect)
  const handleToggle = useCallback(
    (itemId: string) => {
      const isSelected = selectedIds.includes(itemId)
      if (isSelected) {
        onChange(selectedIds.filter((id) => id !== itemId))
      } else {
        onChange([...selectedIds, itemId])
        setSearchQuery('') // clear typed text after adding a selection
      }
      inputRef.current?.focus()
    },
    [selectedIds, onChange]
  )

  // Handle select/deselect all
  const handleToggleAll = useCallback(() => {
    if (allFilteredSelected) {
      // Deselect all filtered items
      const filteredIds = new Set(filteredItems.map((item) => item.id))
      onChange(selectedIds.filter((id) => !filteredIds.has(id)))
    } else {
      // Select all filtered items
      const newSelectedIds = new Set(selectedIds)
      for (const item of filteredItems) {
        newSelectedIds.add(item.id)
      }
      onChange(Array.from(newSelectedIds))
    }
    inputRef.current?.focus()
  }, [allFilteredSelected, filteredItems, selectedIds, onChange])

  // Handle removing a chip
  const handleRemove = useCallback(
    (itemId: string) => {
      onChange(selectedIds.filter((id) => id !== itemId))
      inputRef.current?.focus()
    },
    [selectedIds, onChange]
  )

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          if (!isOpen) {
            setIsOpen(true)
          } else {
            // Start at first item if nothing highlighted, otherwise move down
            setHighlightedIndex((prev) => (prev < 0 ? 0 : Math.min(prev + 1, filteredItems.length - 1)))
          }
          break

        case 'ArrowUp':
          e.preventDefault()
          if (isOpen && highlightedIndex > 0) {
            setHighlightedIndex((prev) => prev - 1)
          }
          break

        case 'Enter':
          e.preventDefault()
          // Only select if an item is highlighted
          if (isOpen && highlightedIndex >= 0 && filteredItems[highlightedIndex]) {
            handleToggle(filteredItems[highlightedIndex].id)
          }
          break

        case 'Escape':
          e.preventDefault()
          setIsOpen(false)
          break

        case 'Backspace':
          // Remove last chip when input is empty
          if (searchQuery === '' && selectedIds.length > 0) {
            handleRemove(selectedIds[selectedIds.length - 1])
          }
          break
      }
    },
    [disabled, isOpen, filteredItems, highlightedIndex, searchQuery, selectedIds, handleToggle, handleRemove]
  )

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlightedElement = listRef.current.querySelector('[data-highlighted="true"]')
      highlightedElement?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex, isOpen])

  // Open dropdown when typing starts
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    if (!isOpen) {
      setIsOpen(true)
    }
  }

  // Focus input when clicking the container
  const handleContainerClick = () => {
    if (!disabled) {
      inputRef.current?.focus()
      setIsOpen(true)
    }
  }

  // Show placeholder with required asterisk appended
  const displayPlaceholder = selectedIds.length === 0 ? (required ? `${placeholder} \u200B*` : placeholder) : ''

  return (
    <div data-ds-component="Typeahead" data-testid="typeahead" className={clsx('flex flex-col w-full', className)}>
      <Popover.Root open={isOpen && !disabled} onOpenChange={setIsOpen} modal={modal}>
        <Popover.Anchor asChild>
          <div
            onClick={handleContainerClick}
            className={clsx(
              'flex items-start gap-2 min-h-[44px] w-full pl-3 pr-3 py-2.5',
              'bg-white rounded border',
              isOpen ? 'border-brand-400' : 'border-brand-neutral-400',
              'transition-colors duration-200',
              'cursor-text',
              disabled && 'opacity-50 cursor-not-allowed bg-brand-neutral-100'
            )}
          >
            {/* Leading icon - small by default */}
            {leadingIcon && <div className="shrink-0 size-5 text-brand-neutral-600">{leadingIcon}</div>}

            {/* Wrapping area for chips and input - max 4 rows (~152px), then scroll */}
            <div
              className={clsx(
                'flex-1 min-w-0 flex flex-wrap items-center gap-2',
                'max-h-[152px] overflow-y-auto',
                // Hide scrollbar for cleaner look
                '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'
              )}
            >
              {/* Selected chips - show description if available, otherwise label */}
              {selectedIds.map((id) => {
                const item = selectedItemsMap.get(id)
                if (!item) return null
                const chipLabel = item.description || item.label
                return <TypeaheadChip key={id} label={chipLabel} onRemove={() => handleRemove(id)} disabled={disabled} />
              })}

              {/* Search input */}
              <input
                ref={inputRef}
                id={inputId}
                type="text"
                value={searchQuery}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => !disabled && setIsOpen(true)}
                placeholder={displayPlaceholder}
                disabled={disabled}
                autoFocus={autoFocus}
                // Search/filter input - browser autofill would conflict with the dropdown
                autoComplete="off"
                role="combobox"
                aria-label={ariaLabel || placeholder}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-autocomplete="list"
                // Only reference the listbox while it exists in the DOM (popover open)
                aria-controls={isOpen ? listboxId : undefined}
                aria-required={required}
                className={clsx(
                  'flex-1 min-w-[80px] shrink-0 bg-transparent',
                  'border-0 p-0 m-0 ring-0 shadow-none',
                  'focus:outline-none focus:ring-0 focus:shadow-none',
                  'text-base text-brand-neutral-950 placeholder:text-brand-neutral-600 leading-6 tracking-[-0.32px]',
                  'disabled:cursor-not-allowed'
                )}
              />
            </div>

            {/* Chevron icon - aligned with first row */}
            <ChevronDown className="h-6 w-6 shrink-0 text-brand-neutral-600" aria-hidden />
          </div>
        </Popover.Anchor>

        <Popover.Portal>
          <Popover.Content
            className={clsx(
              'w-[var(--radix-popover-trigger-width)] rounded-lg border border-brand-neutral-200 bg-white',
              'shadow-menu',
              // z-[1001] appears above modals (z-[1000]) - consistent with other dropdowns in codebase
              'z-[1001] max-h-[350px] overflow-y-auto'
            )}
            sideOffset={4}
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {/* No results */}
            <div ref={listRef} id={listboxId} role="listbox" aria-label="Options" className="p-2">
              {filteredItems.length === 0 ? (
                <div className="px-2 py-5 text-center">
                  {/* Empty list: show emptyStateTitle. No results from search: show noResultsTitle with searchTerm */}
                  {items.length === 0 ? (
                    <>
                      {emptyStateTitle && <p className="text-sm font-semibold text-brand-neutral-950">{emptyStateTitle}</p>}
                      {emptyStateDescription && <p className={clsx('text-sm text-brand-neutral-800', emptyStateTitle && 'mt-1')}>{emptyStateDescription}</p>}
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-brand-neutral-950">{noResultsTitle.replace('{searchTerm}', searchQuery)}</p>
                      {noResultsDescription && <p className="text-sm text-brand-neutral-800 mt-1">{noResultsDescription}</p>}
                    </>
                  )}
                  {emptyStateAction && <div className="mt-2 flex justify-center">{emptyStateAction}</div>}
                </div>
              ) : (
                <>
                  {/* Select all header */}
                  {showSelectAll && (
                    <div
                      role="option"
                      aria-selected={allFilteredSelected}
                      onClick={handleToggleAll}
                      className="flex items-center gap-4 h-8 pl-2 cursor-pointer"
                    >
                      <Checkbox.Root
                        checked={allFilteredSelected ? true : someFilteredSelected ? 'indeterminate' : false}
                        tabIndex={-1}
                        className={clsx(
                          'h-4 w-4 shrink-0 rounded flex items-center justify-center',
                          allFilteredSelected ? 'bg-brand-500 border-brand-500' : 'bg-white border border-brand-neutral-300'
                        )}
                      >
                        <Checkbox.Indicator>
                          <CheckIcon className="h-3 w-3 text-white" />
                        </Checkbox.Indicator>
                      </Checkbox.Root>
                      <span className="text-xs font-semibold text-brand-neutral-500 uppercase leading-4">{formattedSelectAllLabel}</span>
                    </div>
                  )}

                  {/* Option items */}
                  {filteredItems.map((item, index) => (
                    <TypeaheadOption
                      key={item.id}
                      id={item.id}
                      label={item.label}
                      description={item.description}
                      selected={selectedIds.includes(item.id)}
                      highlighted={index === highlightedIndex}
                      onToggle={() => handleToggle(item.id)}
                    />
                  ))}
                </>
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Helper text */}
      {helperText && <div className="mt-2 px-3 py-2 bg-brand-neutral-100 rounded text-xs font-medium text-brand-neutral-600">{helperText}</div>}
    </div>
  )
}
